import { isSketchContour } from '../../mesh';
import * as THREE from 'three/webgpu';
import {
    AbstractInteractionVisualState,
    GeometryInteractionState,
    InteractionVisualStateContext,
    InteractionVisualStateRuntime,
    MaterialApplySource,
} from './AbstractInteractionVisualState';
import type {Viewer} from '../../viewer/Viewer';

/**
 * 草图裁剪视觉状态。
 * 说明：
 * 1. 保留 hover 高亮；
 * 2. 不处理默认选择高亮；
 * 3. 不显示关系标签；
 * 4. 用于裁剪模式下的“悬浮预判”反馈。
 */
export class SketchTrimTransitionState extends AbstractInteractionVisualState {
    /**
     * 构造函数。
     * @param runtime 状态运行时。
     * @param viewer Viewer 实例。
     */
    public constructor(runtime: InteractionVisualStateRuntime, viewer: Viewer) {
        super(runtime, viewer);
    }

    /**
     * 刷新视觉状态（裁剪模式专用）。
     * @param context 当前刷新上下文。
     */
    public override refreshVisualState(context: InteractionVisualStateContext): void {
        this.restoreAllStyledMaterials();
        this.runtime.objectStateMachine.clear();
        context.relationLabelState.clearAllMarkers();
        context.relationLabelState.clearMarkerVisualStates();
        context.syncedMarkerObjectIdSet.clear();

        //------ 裁剪模式选中态可视化 开始------
        const selectedObjectUuidSet = new Set<string>();
        context.selectedObjects.forEach((object) => {
            selectedObjectUuidSet.add(object.uuid);
            this.setObjectState(
                object,
                GeometryInteractionState.Selected,
                MaterialApplySource.Direct,
                context,
            );
        });
        this.applySelectedLineControlPointState(context, selectedObjectUuidSet);
        //------ 裁剪模式选中态可视化 结束------

        this.runtime.externalStateHints.forEach((hintState, objectUuid) => {
            const materialSnapshot = this.runtime.originalMaterialSnapshots.get(objectUuid);
            if (!materialSnapshot) {
                return;
            }
            this.setObjectState(
                materialSnapshot.object,
                hintState,
                MaterialApplySource.Direct,
                context,
            );
        });

        if (!context.hoveredObject) {
            return;
        }

        const allowHover = this.canHoverPointInDrawingMode(context.hoveredObject, context);
        if (!allowHover) {
            return;
        }

        if (!selectedObjectUuidSet.has(context.hoveredObject.uuid) && !isSketchContour(context.hoveredObject)) {
            this.setObjectState(
                context.hoveredObject,
                GeometryInteractionState.Hover,
                MaterialApplySource.Direct,
                context,
            );
        }

        this.applyLineControlPointHoverState(context, context.hoveredObject, selectedObjectUuidSet);
        this.highlightContourLinkedEdgesOnHover(context, context.hoveredObject);
    }

    //------ 裁剪模式禁用轮廓移入高亮 开始------
    /**
     * 复写父类轮廓联动高亮逻辑。
     * 裁剪模式下不对轮廓及其关联边执行移入高亮。
     * @param context 当前刷新上下文。
     * @param hoveredObject 当前移入对象。
     */
    protected override highlightContourLinkedEdgesOnHover(
        context: InteractionVisualStateContext,
        hoveredObject: THREE.Object3D,
    ): void {
        void context;
        void hoveredObject;
    }
    //------ 裁剪模式禁用轮廓移入高亮 结束------
}
